<script setup lang="ts">
import { ElIcon } from 'element-plus'
import { computed, ref, watch } from 'vue'

import ChevronIcon from '../../assets/icons/chevron.svg?component'
import FirstPageIcon from '../../assets/icons/first-page.svg?component'
import LastPageIcon from '../../assets/icons/last-page.svg?component'

import FlexComponent from './FlexComponent.vue'
import TooltipComponent from './TooltipComponent.vue'

const props = defineProps<{ totalPages: number }>()

const currentPage = defineModel<number>('currentPage', { default: 1 })

const lastPage = computed(() => Math.max(props.totalPages, 1))
const isFirstPage = computed(() => currentPage.value <= 1)
const isLastPage = computed(() => currentPage.value >= lastPage.value)

const goToPage = (page: number) => {
  currentPage.value = Math.min(Math.max(page, 1), lastPage.value)
}

// An unknown (0) page count is usually a list mid-load, so only a real count pulls the page back.
// Immediate, because most lists only render the pagination once the count is known.
watch(
  () => props.totalPages,
  (totalPages) => {
    if (totalPages >= 1 && currentPage.value > totalPages) {
      goToPage(totalPages)
    }
  },
  { immediate: true },
)

const inputValue = ref(String(currentPage.value))
watch(currentPage, (page) => (inputValue.value = String(page)))

const commitInput = () => {
  const page = Number.parseInt(inputValue.value, 10)

  if (Number.isNaN(page)) {
    inputValue.value = String(currentPage.value)
    return
  }

  goToPage(page)
  inputValue.value = String(currentPage.value)
}
</script>

<template>
  <FlexComponent v-if="totalPages > 1" justify="center" align="center" :gap="24" class="pagination">
    <FlexComponent align="center" :gap="8" :class="{ 'pagination__side--idle': isFirstPage }">
      <button type="button" class="pagination__button" aria-label="First page" @click="goToPage(1)">
        <ElIcon :size="18">
          <FirstPageIcon />
        </ElIcon>
      </button>
      <button type="button" class="pagination__button" aria-label="Previous page" @click="goToPage(currentPage - 1)">
        <ElIcon :size="18">
          <ChevronIcon class="pagination__chevron--previous" />
        </ElIcon>
      </button>
    </FlexComponent>

    <input
      v-model="inputValue"
      type="text"
      inputmode="numeric"
      aria-label="Page"
      class="pagination__input"
      @keyup.enter="commitInput"
      @blur="commitInput"
    />

    <FlexComponent align="center" :gap="8" :class="{ 'pagination__side--idle': isLastPage }">
      <button type="button" class="pagination__button" aria-label="Next page" @click="goToPage(currentPage + 1)">
        <ElIcon :size="18">
          <ChevronIcon class="pagination__chevron--next" />
        </ElIcon>
      </button>
      <TooltipComponent placement="top" :show-arrow="false">
        <button type="button" class="pagination__button" aria-label="Last page" @click="goToPage(totalPages)">
          <ElIcon :size="18">
            <LastPageIcon />
          </ElIcon>
        </button>
        <template #content>Page {{ totalPages }}</template>
      </TooltipComponent>
    </FlexComponent>
  </FlexComponent>
</template>

<style scoped lang="scss">
.pagination__side--idle {
  visibility: hidden;
}

.pagination__button {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  padding: 3px;
  border: none;
  border-radius: 48px;
  background: var(--button-hover);
  color: var(--primary);
  cursor: pointer;
  transition: background var(--transition) ease;

  &:hover {
    background: var(--secondary-12);
  }
}

.pagination__chevron--previous {
  transform: rotate(90deg);
}

.pagination__chevron--next {
  transform: rotate(-90deg);
}

.pagination__input {
  width: 48px;
  height: 32px;
  padding: 6px 4px;
  border: 1px solid var(--button-hover);
  border-radius: 8px;
  background: var(--inverted);

  font-family: inherit;
  font-size: 14px;
  line-height: 20px;
  color: var(--primary);
  text-align: center;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
}

:deep(.el-icon) {
  path {
    fill: currentColor;
  }
}
</style>
